import React, { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// Magnetic button - pulls cursor when nearby
export const MagneticButton = ({ children, className = "", as: Tag = "button", strength = 0.35, ...props }) => {
    const ref = useRef(null);
    const xTo = useRef(null);
    const yTo = useRef(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        xTo.current = gsap.quickTo(el, "x", { duration: 0.5, ease: "elastic.out(1, 0.4)" });
        yTo.current = gsap.quickTo(el, "y", { duration: 0.5, ease: "elastic.out(1, 0.4)" });

        const onMove = (e) => {
            const r = el.getBoundingClientRect();
            const x = (e.clientX - (r.left + r.width / 2)) * strength;
            const y = (e.clientY - (r.top + r.height / 2)) * strength;
            xTo.current(x);
            yTo.current(y);
        };
        const onLeave = () => { xTo.current(0); yTo.current(0); };
        el.addEventListener("mousemove", onMove);
        el.addEventListener("mouseleave", onLeave);
        return () => {
            el.removeEventListener("mousemove", onMove);
            el.removeEventListener("mouseleave", onLeave);
        };
    }, [strength]);

    return <Tag ref={ref} className={className} {...props}>{children}</Tag>;
};

// Word-by-word reveal heading
export const RevealHeading = ({ children, className = "", as: Tag = "h2", delay = 0, stagger = 0.06 }) => {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        const words = el.querySelectorAll("[data-word]");
        gsap.set(words, { yPercent: 110, opacity: 0 });
        const trig = ScrollTrigger.create({
            trigger: el,
            start: "top 82%",
            once: true,
            onEnter: () => {
                gsap.to(words, { yPercent: 0, opacity: 1, duration: 0.9, ease: "power3.out", stagger, delay });
            },
        });
        return () => trig.kill();
    }, [delay, stagger]);

    const text = typeof children === "string" ? children : "";
    if (!text) {
        return <Tag ref={ref} className={className}>{children}</Tag>;
    }
    const words = text.split(" ");
    return (
        <Tag ref={ref} className={className} aria-label={text}>
            {words.map((w, i) => (
                <span key={i} className="inline-block overflow-hidden align-bottom">
                    <span data-word className="inline-block will-change-transform">{w}{i < words.length - 1 ? "\u00A0" : ""}</span>
                </span>
            ))}
        </Tag>
    );
};

// Stagger reveal children on scroll
export const StaggerReveal = ({ children, className = "", y = 32, stagger = 0.08, start = "top 80%" }) => {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        const items = el.querySelectorAll("[data-stagger]");
        gsap.set(items, { y, opacity: 0 });
        const trig = ScrollTrigger.create({
            trigger: el,
            start,
            once: true,
            onEnter: () => gsap.to(items, { y: 0, opacity: 1, duration: 0.8, ease: "power3.out", stagger }),
        });
        return () => trig.kill();
    }, [y, stagger, start]);
    return <div ref={ref} className={className}>{children}</div>;
};

// Count up animation
export const CountUp = ({ to = 100, prefix = "", suffix = "", duration = 2, className = "", decimals = 0 }) => {
    const ref = useRef(null);
    const [value, setValue] = useState(0);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            setValue(to); return;
        }
        const obj = { v: 0 };
        const trig = ScrollTrigger.create({
            trigger: el,
            start: "top 80%",
            once: true,
            onEnter: () => {
                gsap.to(obj, {
                    v: to,
                    duration,
                    ease: "power2.out",
                    onUpdate: () => setValue(obj.v),
                });
            },
        });
        return () => trig.kill();
    }, [to, duration]);
    const formatted = decimals > 0 ? value.toFixed(decimals) : Math.floor(value).toLocaleString();
    return <span ref={ref} className={className}>{prefix}{formatted}{suffix}</span>;
};

// Parallax wrapper for floating decorations
export const Parallax = ({ children, speed = 0.2, className = "", style }) => {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        const trig = gsap.to(el, {
            yPercent: speed * 100,
            ease: "none",
            scrollTrigger: {
                trigger: el,
                start: "top bottom",
                end: "bottom top",
                scrub: true,
            },
        });
        return () => { if (trig.scrollTrigger) trig.scrollTrigger.kill(); trig.kill(); };
    }, [speed]);
    return <div ref={ref} className={className} style={style}>{children}</div>;
};
